import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AuthService } from './auth.service';
import {
  BillingInterval,
  FounderOffer,
  Subscription,
} from '../models/subscription.model';

/**
 * DÉTAILS: Ce service est le point de communication central avec notre API backend.
 * Il abstrait la logique des requêtes HTTP (GET, POST, etc.) et gère
 * l'ajout automatique du token d'authentification.
 */
@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  // DÉTAILS: L'URL de base de notre API backend. Doit être mise dans les fichiers d'environnement.
  //private apiUrl = 'http://analyticai.com:3000/api';
  private apiUrl = '/api';

  /**
   * DÉTAILS: Crée les en-têtes HTTP pour les requêtes.
   * Si un token est disponible, il l'ajoute au header 'Authorization'.
   * @returns Un objet HttpHeaders.
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (token) {
      return new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
    }
    return new HttpHeaders({ 'Content-Type': 'application/json' });
  }

  // --- Méthodes pour les Abonnements ---

  /**
   * Crée une session de paiement Stripe Checkout.
   * @param planId L'identifiant du plan (ex: 'pro').
   * @returns Un observable avec l'objet session de Stripe contenant l'ID de session.
   */
  createCheckoutSession(
    planId: string,
  ): Observable<{ id: string; url: string }> {
    const headers = this.getAuthHeaders();
    return this.http.post<{ id: string; url: string }>(
      `${this.apiUrl}/subscriptions/create-checkout-session`,
      { planId },
      { headers },
    );
  }

  // Pour un changement de plan (Payant A -> Payant B) ou un premier abonnement (mensuel ou annuel)
  updateSubscription(
    newPlanId: string,
    interval: BillingInterval = 'month',
  ): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.put(
      `${this.apiUrl}/subscriptions/update-plan`,
      { newPlanId, interval },
      { headers },
    );
  }

  // Etat réel de l'offre fondateur (-30 % à vie) : public, utilisé par la landing
  getFounderOffer(): Observable<FounderOffer> {
    return this.http.get<FounderOffer>(`${this.apiUrl}/plans/founder-offer`);
  }

  // Pour annuler l'abonnement
  cancelSubscription(reason: string): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post(
      `${this.apiUrl}/subscriptions/cancel`,
      { reason },
      { headers },
    );
  }

  // Annule une résiliation programmée (avant la fin de période)
  reactivateSubscription(): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post(
      `${this.apiUrl}/subscriptions/reactivate`,
      {},
      { headers },
    );
  }

  // Met l'abonnement payant en pause (fin de période payée, données conservées)
  pauseSubscription(): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post(
      `${this.apiUrl}/subscriptions/pause`,
      {},
      { headers },
    );
  }

  // Annule une pause programmée, ou reprend (Checkout payant) une pause effective
  resumeSubscription(
    planId?: string,
    interval?: BillingInterval,
  ): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post(
      `${this.apiUrl}/subscriptions/resume`,
      { planId, interval },
      { headers },
    );
  }

  /**
   * Récupère les informations de l'abonnement de l'utilisateur connecté.
   * @returns Un observable avec les détails de l'abonnement.
   */
  getSubscription(): Observable<Subscription> {
    const headers = this.getAuthHeaders();
    return this.http.get<Subscription>(`${this.apiUrl}/subscriptions/me`, {
      headers,
    });
  }

  /**
   * Vérifie une session Stripe Checkout et active l'abonnement côté backend.
   */
  verifyCheckoutSession(sessionId: string): Observable<any> {
    const headers = this.getAuthHeaders();
    return this.http.post(
      `${this.apiUrl}/subscriptions/verify-checkout`,
      { sessionId },
      { headers },
    );
  }

  /**
   * DÉTAILS: Crée une session de portail client Stripe.
   * @returns Un observable avec l'objet de session contenant l'URL de redirection.
   */
  createPortalSession(): Observable<{ url: string }> {
    const headers = this.getAuthHeaders();
    return this.http.post<{ url: string }>(
      `${this.apiUrl}/subscriptions/create-portal-session`,
      {},
      { headers },
    );
  }

  uploadDocument(formData: FormData, token: string) {
    const headers = { Authorization: `Bearer ${token}` };
    return this.http.post(`${this.apiUrl}/upload`, formData, { headers });
  }

  // --- Ici, vous ajouteriez d'autres méthodes pour interagir avec d'autres parties de l'API ---
  // Par exemple :
  // getDocuments(): Observable<any> { ... }
  // uploadDocument(file: File): Observable<any> { ... }
}
